import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, Select, Switch, Space, message, Popconfirm, Tag, Upload, Alert, Card, Row, Col, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

function BillInvoice() {
  const { canEdit } = useMenuPermission();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.username === 'admin';
  const isSuperAdmin = user.username === 'admin';
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({ team: '', supplier_id: '', is_paid: '', is_invoiced: '', is_collected: '', payee_supplier: '', pay_supplier: '', start_date: '', end_date: '', min_amount: '', max_amount: '' });
  // 批量修改
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchEditVisible, setBatchEditVisible] = useState(false);
  const [batchEditField, setBatchEditField] = useState(null);
  const [batchEditValue, setBatchEditValue] = useState(null);
  const [batchEditSaving, setBatchEditSaving] = useState(false);
  // 批量导入
  const [importVisible, setImportVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // 打款/收款主体历史记录（仅超管使用）
  const [payeeHistory, setPayeeHistory] = useState([]);
  const [payHistory, setPayHistory] = useState([]);

  const loadData = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, pageSize: 50 };
      if (keyword) params.keyword = keyword;
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== undefined) params[k] = v; });
      const res = await api.get('/bill-invoices', { params });
      if (res.code === 200) { setData(res.data.list); setTotal(res.data.total); setPage(p); }
    } catch (e) {}
    setLoading(false);
  };

  const loadSuppliers = async () => {
    try { const r = await api.get('/suppliers/all'); if (r.code === 200) setSuppliers(r.data || []); } catch (e) {}
  };

  const [billPermMap, setBillPermMap] = useState({});
  const loadBillPerms = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.username === 'admin') return;
    try {
      let res;
      if (user.role === 'supplier' && user.supplier_id) {
        res = await api.get('/suppliers/my/permissions');
        if (res.code === 200 && res.data && res.data.bill_fields) {
          const map = {};
          res.data.bill_fields.forEach(f => { map[f.field_name] = f.is_visible === 1; });
          setBillPermMap(map);
        }
      } else {
        res = await api.get(`/users/${user.id}/bill-fields`);
        if (res.code === 200) {
          const map = {};
          (res.data || []).forEach(f => { map[f.field_name] = f.is_visible === 1; });
          setBillPermMap(map);
        }
      }
    } catch (e) {}
  };

  useEffect(() => { loadData(); loadSuppliers(); loadBillPerms(); }, []);

  // 检查账单字段是否可见（有权限配置时使用，无配置默认可见）
  const isBillFieldVisible = (fieldName) => {
    if (Object.keys(billPermMap).length === 0) return true;
    return billPermMap[fieldName] !== false;
  };

  // 加载账单可编辑权限
  const [billEditMap, setBillEditMap] = useState({});
  const loadBillEditPerms = async () => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u.username === 'admin') return;
    try {
      let res;
      if (u.role === 'supplier' && u.supplier_id) {
        res = await api.get('/suppliers/my/permissions');
      } else {
        res = await api.get(`/users/${u.id}/bill-fields`);
      }
      if (res.code === 200) {
        const data = res.data.bill_fields || res.data || [];
        const map = {};
        data.forEach(f => { map[f.field_name] = f.is_editable === 1; });
        setBillEditMap(map);
      }
    } catch (e) {}
  };
  useEffect(() => { loadBillEditPerms(); }, []);

  const isBillFieldEditable = (fieldName) => {
    if (JSON.parse(localStorage.getItem('user') || '{}').username === 'admin') return true;
    if (Object.keys(billEditMap).length === 0) return false;
    return billEditMap[fieldName] === true;
  };

  // 是否有任何账单字段可编辑
  const hasAnyBillEditPerm = () => {
    if (JSON.parse(localStorage.getItem('user') || '{}').username === 'admin') return true;
    if (Object.keys(billEditMap).length === 0) return false;
    return Object.values(billEditMap).some(v => v === true);
  };

  // 解析结算日期字符串 → Form.List 数组
  // 单个日期(无_)回填为同日起止；范围 start_end 正常解析
  const parseSettlementDates = (str) => {
    if (!str) return [];
    return str.split(',').map(range => {
      const [start, end] = range.split('_');
      if (!start) return null;
      return { range: [dayjs(start), dayjs(end || start)] };
    }).filter(Boolean);
  };

  // 拼接 Form.List 数组 → 保存字符串
  // 起止同一天 = 单日，存 "2026-05-28"；不同 = 范围，存 "start_end"；多个用逗号分隔
  const joinSettlementDates = (items) => {
    if (!items || !items.length) return null;
    const parts = items.filter(item => item && item.range && item.range[0] && item.range[1])
      .map(item => {
        const start = item.range[0].format('YYYY-MM-DD');
        const end = item.range[1].format('YYYY-MM-DD');
        return start === end ? start : `${start}_${end}`;
      });
    return parts.length > 0 ? parts.join(',') : null;
  };

  const handleAdd = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ is_paid: false, is_invoiced: false, is_collected: false }); setModalVisible(true); };
  const handleEdit = (r) => { setEditing(r); form.setFieldsValue({ ...r, settlement_date_ranges: parseSettlementDates(r.settlement_date) }); setModalVisible(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const v = await form.validateFields();
      const payload = { ...v, settlement_date: joinSettlementDates(v.settlement_date_ranges) };
      delete payload.settlement_date_ranges;
      if (editing) await api.put(`/bill-invoices/${editing.id}`, payload);
      else await api.post('/bill-invoices', payload);
      message.success(editing ? '更新成功' : '新增成功');
      setModalVisible(false); loadData(); loadSubjectHistory();
    } catch (e) {}
    setSaving(false);
  };

  const handleDelete = async (id) => { await api.delete(`/bill-invoices/${id}`); message.success('删除成功'); loadData(); };

  // ===== 批量修改 =====
  const handleBatchEdit = async () => {
    if (!selectedRowKeys.length) { message.warning('请先勾选账单'); return; }
    if (!batchEditField) { message.warning('请选择要修改的字段'); return; }
    setBatchEditSaving(true);
    try {
      const res = await api.put('/bill-invoices/batch/field', { ids: selectedRowKeys, field: batchEditField, value: batchEditValue });
      if (res.code === 200) { message.success(res.message); setBatchEditVisible(false); setSelectedRowKeys([]); setBatchEditField(null); setBatchEditValue(null); loadData(); }
    } catch (e) { }
    setBatchEditSaving(false);
  };

  // ===== 批量导入 =====
  const handleImportPreview = async () => {
    if (!importFile) { message.warning('请选择Excel文件'); return; }
    setImportLoading(true);
    const fd = new FormData(); fd.append('file', importFile);
    try {
      const res = await api.post('/bill-invoices/batch/import-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.code === 200) { setImportPreview(res.data); message.success(`预览完成，共 ${res.data.total} 条`); }
    } catch (e) { }
    setImportLoading(false);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImportLoading(true);
    const fd = new FormData(); fd.append('file', importFile);
    try {
      const res = await api.post('/bill-invoices/batch/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.code === 200) { setImportResult(res.data); setImportPreview(null); setImportFile(null); message.success(res.message); loadData(); loadSubjectHistory(); }
    } catch (e) { }
    setImportLoading(false);
  };

  // ===== 打款/收款主体历史记录 =====
  const loadSubjectHistory = async () => {
    if (!isSuperAdmin) return;
    try {
      const [r1, r2] = await Promise.all([
        api.get('/bill-invoices/subject-history', { params: { field_type: 'payee_supplier' } }),
        api.get('/bill-invoices/subject-history', { params: { field_type: 'pay_supplier' } })
      ]);
      if (r1.code === 200) setPayeeHistory(r1.data || []);
      if (r2.code === 200) setPayHistory(r2.data || []);
    } catch (e) { }
  };
  useEffect(() => { loadSubjectHistory(); }, []);

  const handleDeleteHistory = async (id, fieldType) => {
    try {
      const res = await api.delete(`/bill-invoices/subject-history/${id}`);
      if (res.code === 200) { message.success('删除成功'); loadSubjectHistory(); }
    } catch (e) { }
  };

  // ===== 导出Excel（按当前筛选条件） =====
  const handleExport = async () => {
    try {
      const params = {};
      if (keyword) params.keyword = keyword;
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== undefined && v !== null) params[k] = v; });
      const res = await api.get('/bill-invoices/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bill_invoices_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (err) {
      message.error('导出失败');
    }
  };

  // 构建历史选项（右侧带删除按钮，仅超管）
  const buildSubjectOptions = (historyList, fieldType) =>
    (historyList || []).map(h => ({
      value: h.name,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{h.name}</span>
          <DeleteOutlined
            style={{ color: '#999', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); handleDeleteHistory(h.id, fieldType); }}
          />
        </div>
      )
    }));

  const handleUpload = async (record, file) => {
    const fd = new FormData(); fd.append('file', file);
    fd.append('id', record.id);
    fd.append('settlement_date', record.settlement_date || '');
    fd.append('amount', record.amount || '');
    fd.append('payee_supplier', record.payee_supplier || '');
    fd.append('pay_supplier', record.pay_supplier || '');
    try {
      const res = await fetch('/api/bill-invoices/upload', { method: 'POST', body: fd, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      if (data.code === 200) { message.success('上传成功'); loadData(); }
      else { message.error('上传失败'); }
    } catch (e) { message.error('上传失败'); }
    return false;
  };

  const allCols = [
    { key: 'bill_team', title: '小组', dataIndex: 'team', width: 100 },
    { key: 'bill_settlement_date', title: '结算日期', dataIndex: 'settlement_date', width: 180,
      render: v => {
        if (!v) return '-';
        return (
          <Space size={2} wrap>
            {String(v).split(',').map((r, i) => {
              const parts = r.split('_');
              return <Tag key={i} color="blue">{parts.length > 1 ? `${parts[0]} ~ ${parts[1]}` : r}</Tag>;
            })}
          </Space>
        );
      }
    },
    { key: 'bill_amount', title: '金额', dataIndex: 'amount', width: 100, align: 'right', render: v => v != null ? `¥${Number(v).toFixed(2)}` : '-' },
    { key: 'bill_is_paid', title: '是否打款', dataIndex: 'is_paid', width: 80, render: v => v ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
    { key: 'bill_is_invoiced', title: '是否开票', dataIndex: 'is_invoiced', width: 80, render: v => v ? <Tag color="blue">是</Tag> : <Tag>否</Tag> },
    { key: 'bill_is_collected', title: '是否回单', dataIndex: 'is_collected', width: 80, render: v => v ? <Tag color="purple">是</Tag> : <Tag>否</Tag> },
    { key: 'bill_supplier_id', title: '主体', dataIndex: 'supplier_name', width: 100 },
    { key: 'bill_payee_supplier', title: '打款主体', dataIndex: 'payee_supplier', width: 120 },
    { key: 'bill_pay_supplier', title: '收款主体', dataIndex: 'pay_supplier', width: 120 },
    { key: 'bill_remark', title: '备注', dataIndex: 'remark', width: 160, ellipsis: true },
    { key: 'bill_invoice_file', title: '发票', dataIndex: 'invoice_file', width: 170,
      render: (v, r) => {
        const filename = v ? v.split('/').pop() : '';
        return (
          <div>
            <Space size={4}>
              {v && <a href={v} target="_blank" download><Button size="small" icon={<DownloadOutlined />}>下载</Button></a>}
              {isBillFieldEditable('bill_invoice_file') && <Upload showUploadList={false} beforeUpload={f => handleUpload(r, f)}><Button size="small" icon={<UploadOutlined />}>{v ? '重传' : '上传'}</Button></Upload>}
            </Space>
            {v && <div style={{ fontSize: 12, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160, marginTop: 2 }} title={filename}>{filename}</div>}
          </div>
        );
      }
    },
    { title: '操作', width: 140,
      render: (_, r) => <Space>
        {canEdit && hasAnyBillEditPerm() && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>}
        {isSuperAdmin && <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}><Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button></Popconfirm>}
      </Space>
    }
  ];

  const cols = [{ title: 'ID', dataIndex: 'id', width: 60 }, ...allCols.filter(c => !c.key || isBillFieldVisible(c.key))];

  return (
    <div>
      <h3 style={{ margin: '0 0 16px 0' }}>账单发票</h3>
      <Card size="small" title="筛选条件" style={{ marginBottom: 12 }}
        extra={
          <Space size={4}>
            <Button type="primary" size="small" onClick={() => loadData(1)}>查询</Button>
            <Button size="small" onClick={() => { setFilters({ team:'', supplier_id:'', is_paid:'', is_invoiced:'', is_collected:'', payee_supplier:'', pay_supplier:'', start_date:'', end_date:'', min_amount:'', max_amount:'' }); setKeyword(''); }}>重置</Button>
          </Space>
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Input placeholder="关键词搜索" prefix={<SearchOutlined />} value={keyword} onChange={e => setKeyword(e.target.value)} onPressEnter={() => loadData(1)} allowClear />
          </Col>
          {isBillFieldVisible('bill_team') && (
            <Col xs={12} sm={8} md={6} lg={4}>
              <Input placeholder="小组" value={filters.team} onChange={e => setFilters(p => ({ ...p, team: e.target.value }))} allowClear />
            </Col>
          )}
          {isBillFieldVisible('bill_supplier_id') && (
            <Col xs={12} sm={8} md={6} lg={4}>
              <Select placeholder="主体" value={filters.supplier_id || undefined} onChange={v => setFilters(p => ({ ...p, supplier_id: v || '' }))} style={{ width: '100%' }} allowClear options={suppliers.map(s => ({ label: s.name, value: s.id }))} />
            </Col>
          )}
          {isBillFieldVisible('bill_payee_supplier') && (
            <Col xs={12} sm={8} md={6} lg={4}>
              <Input placeholder="打款主体" value={filters.payee_supplier} onChange={e => setFilters(p => ({ ...p, payee_supplier: e.target.value }))} allowClear />
            </Col>
          )}
          {isBillFieldVisible('bill_pay_supplier') && (
            <Col xs={12} sm={8} md={6} lg={4}>
              <Input placeholder="收款主体" value={filters.pay_supplier} onChange={e => setFilters(p => ({ ...p, pay_supplier: e.target.value }))} allowClear />
            </Col>
          )}
        </Row>
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          {isBillFieldVisible('bill_is_paid') && (
            <Col xs={8} sm={6} md={4} lg={3}>
              <Select placeholder="是否打款" value={filters.is_paid || undefined} onChange={v => setFilters(p => ({ ...p, is_paid: v }))} style={{ width: '100%' }} allowClear options={[{ label: '是', value: '1' }, { label: '否', value: '0' }]} />
            </Col>
          )}
          {isBillFieldVisible('bill_is_invoiced') && (
            <Col xs={8} sm={6} md={4} lg={3}>
              <Select placeholder="是否开票" value={filters.is_invoiced || undefined} onChange={v => setFilters(p => ({ ...p, is_invoiced: v }))} style={{ width: '100%' }} allowClear options={[{ label: '是', value: '1' }, { label: '否', value: '0' }]} />
            </Col>
          )}
          {isBillFieldVisible('bill_is_collected') && (
            <Col xs={8} sm={6} md={4} lg={3}>
              <Select placeholder="是否回单" value={filters.is_collected || undefined} onChange={v => setFilters(p => ({ ...p, is_collected: v }))} style={{ width: '100%' }} allowClear options={[{ label: '是', value: '1' }, { label: '否', value: '0' }]} />
            </Col>
          )}
          {isBillFieldVisible('bill_settlement_date') && (
            <Col xs={24} sm={12} md={10} lg={8}>
              <DatePicker.RangePicker
                placeholder={['结算开始日期', '结算结束日期']}
                value={filters.start_date && filters.end_date ? [dayjs(filters.start_date), dayjs(filters.end_date)] : null}
                onChange={(dates) => setFilters(p => ({ ...p, start_date: dates && dates[0] ? dates[0].format('YYYY-MM-DD') : '', end_date: dates && dates[1] ? dates[1].format('YYYY-MM-DD') : '' }))}
                style={{ width: '100%' }}
              />
            </Col>
          )}
          {isBillFieldVisible('bill_amount') && (
            <Col xs={24} sm={12} md={10} lg={8}>
              <Space>
                <InputNumber placeholder="金额≥" min={0} precision={2} value={filters.min_amount || null} onChange={v => setFilters(p => ({ ...p, min_amount: v === null ? '' : v }))} style={{ width: 120 }} />
                <span style={{ color: '#999' }}>至</span>
                <InputNumber placeholder="金额≤" min={0} precision={2} value={filters.max_amount || null} onChange={v => setFilters(p => ({ ...p, max_amount: v === null ? '' : v }))} style={{ width: 120 }} />
              </Space>
            </Col>
          )}
        </Row>
      </Card>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {isAdmin && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd}>新增</Button>}
        {isAdmin && <Button size="small" icon={<UploadOutlined />} onClick={() => { setImportVisible(true); setImportFile(null); setImportPreview(null); setImportResult(null); }}>批量导入</Button>}
        {isAdmin && <Button size="small" icon={<EditOutlined />} onClick={() => { setBatchEditVisible(true); setBatchEditField(null); setBatchEditValue(null); }} disabled={!selectedRowKeys.length}>批量修改{selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}</Button>}
        <Button size="small" icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
      </div>
      <Table rowKey="id" columns={cols} dataSource={data} loading={loading} pagination={{ current: page, pageSize: 50, total, onChange: loadData }} size="small" scroll={{ x: 1550 }}
        rowSelection={isAdmin ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
      />
      <Modal title={editing ? '编辑账单' : '新增账单'} open={modalVisible} onOk={handleSave} onCancel={() => setModalVisible(false)} confirmLoading={saving} width={600}>
        <Form form={form} layout="vertical">
          {isBillFieldEditable('bill_team') && <Form.Item name="team" label="小组"><Input /></Form.Item>}
          {isBillFieldEditable('bill_settlement_date') && (
            <Form.List name="settlement_date_ranges">
              {(fields, { add, remove }) => (
                <div style={{ marginBottom: 24 }}>
                  <Form.Item label="结算日期">
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small" block>添加日期</Button>
                    <div style={{ fontSize: 12, color: '#999' }}>起止选同一天保存为单个日期，不同则保存为日期范围</div>
                  </Form.Item>
                  {fields.map(({ key, name, ...rest }) => (
                    <Form.Item key={key} {...rest} style={{ marginBottom: 8 }}>
                      <Space align="start">
                        <Form.Item name={[name, 'range']} rules={[{ required: true, message: '请选择日期范围' }]} noStyle>
                          <DatePicker.RangePicker />
                        </Form.Item>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      </Space>
                    </Form.Item>
                  ))}
                </div>
              )}
            </Form.List>
          )}
          {isBillFieldEditable('bill_amount') && <Form.Item name="amount" label="金额"><InputNumber style={{ width: '100%' }} precision={2} prefix="¥" /></Form.Item>}
          {isBillFieldEditable('bill_supplier_id') && <Form.Item name="supplier_id" label="主体"><Select allowClear placeholder="选择主体" options={suppliers.map(s => ({ label: s.name, value: s.id }))} /></Form.Item>}
          {isBillFieldEditable('bill_payee_supplier') && (
            <Form.Item name="payee_supplier" label="打款主体">
              {isSuperAdmin
                ? <AutoComplete options={buildSubjectOptions(payeeHistory, 'payee_supplier')} placeholder="输入或选择历史记录" allowClear />
                : <Input />
              }
            </Form.Item>
          )}
          {isBillFieldEditable('bill_pay_supplier') && (
            <Form.Item name="pay_supplier" label="收款主体">
              {isSuperAdmin
                ? <AutoComplete options={buildSubjectOptions(payHistory, 'pay_supplier')} placeholder="输入或选择历史记录" allowClear />
                : <Input />
              }
            </Form.Item>
          )}
          {isBillFieldEditable('bill_remark') && <Form.Item name="remark" label="备注"><Input.TextArea rows={2} /></Form.Item>}
          <Space>
            {isBillFieldEditable('bill_is_paid') && <Form.Item name="is_paid" label="是否打款" valuePropName="checked"><Switch /></Form.Item>}
            {isBillFieldEditable('bill_is_invoiced') && <Form.Item name="is_invoiced" label="是否开票" valuePropName="checked"><Switch /></Form.Item>}
            {isBillFieldEditable('bill_is_collected') && <Form.Item name="is_collected" label="是否回单" valuePropName="checked"><Switch /></Form.Item>}
          </Space>
        </Form>
      </Modal>

      {/* ===== 批量修改弹窗 ===== */}
      <Modal title="批量修改账单" open={batchEditVisible} onOk={handleBatchEdit} onCancel={() => setBatchEditVisible(false)} confirmLoading={batchEditSaving} width={450}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div><b>已选 {selectedRowKeys.length} 条记录</b></div>
          <div>修改字段：</div>
          <Select placeholder="选择要修改的字段" value={batchEditField} onChange={v => { setBatchEditField(v); setBatchEditValue(null); }} style={{ width: '100%' }}
            options={[
              { label: '小组', value: 'team' },
              { label: '结算日期', value: 'settlement_date' },
              { label: '金额', value: 'amount' },
              { label: '是否打款', value: 'is_paid' },
              { label: '是否开票', value: 'is_invoiced' },
              { label: '是否回单', value: 'is_collected' },
              { label: '主体', value: 'supplier_id' },
              { label: '打款主体', value: 'payee_supplier' },
              { label: '收款主体', value: 'pay_supplier' },
              { label: '备注', value: 'remark' }
            ]}
          />
          {batchEditField && (
            <div>新值：</div>
          )}
          {batchEditField && (batchEditField === 'is_paid' || batchEditField === 'is_invoiced' || batchEditField === 'is_collected') && (
            <Select placeholder="选择" value={batchEditValue} onChange={setBatchEditValue} style={{ width: '100%' }}
              options={[{ label: '是', value: 1 }, { label: '否', value: 0 }]}
            />
          )}
          {batchEditField && batchEditField === 'amount' && (
            <InputNumber style={{ width: '100%' }} precision={2} prefix="¥" value={batchEditValue} onChange={setBatchEditValue} />
          )}
          {batchEditField && batchEditField === 'supplier_id' && (
            <Select placeholder="选择主体" value={batchEditValue} onChange={setBatchEditValue} style={{ width: '100%' }} allowClear
              options={suppliers.map(s => ({ label: s.name, value: s.id }))}
            />
          )}
          {batchEditField && batchEditField === 'remark' && (
            <Input.TextArea placeholder="输入备注内容" rows={3} value={batchEditValue || ''} onChange={e => setBatchEditValue(e.target.value)} />
          )}
          {batchEditField && batchEditField !== 'is_paid' && batchEditField !== 'is_invoiced' && batchEditField !== 'is_collected' && batchEditField !== 'amount' && batchEditField !== 'supplier_id' && batchEditField !== 'remark' && (
            <Input placeholder="输入新值" value={batchEditValue || ''} onChange={e => setBatchEditValue(e.target.value)} />
          )}
        </Space>
      </Modal>

      {/* ===== 批量导入弹窗 ===== */}
      <Modal title="批量导入账单" open={importVisible} onCancel={() => setImportVisible(false)} width={800}
        footer={[
          <Button key="cancel" onClick={() => setImportVisible(false)}>关闭</Button>,
          importPreview && !importResult && <Button key="import" type="primary" loading={importLoading} onClick={handleImportSubmit}>确认导入</Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Upload accept=".xlsx,.xls" beforeUpload={file => { setImportFile(file); setImportPreview(null); setImportResult(null); return false; }}
            fileList={importFile ? [importFile] : []} onRemove={() => { setImportFile(null); setImportPreview(null); setImportResult(null); }} maxCount={1}>
            <Button icon={<UploadOutlined />} loading={importLoading}>选择Excel文件</Button>
          </Upload>
          {importFile && !importPreview && (
            <Button type="primary" onClick={handleImportPreview} loading={importLoading}>预览数据</Button>
          )}
          {importPreview && (
            <div>
              <Alert type="info" message={`共 ${importPreview.total} 条数据，请确认后点击"确认导入"`} style={{ marginBottom: 12 }} />
              <Table rowKey="_row" dataSource={importPreview.preview} columns={[
                { title: '行', dataIndex: '_row', width: 50 },
                { title: '小组', dataIndex: 'team', width: 80 },
                { title: '结算日期', dataIndex: 'settlement_date', width: 180 },
                { title: '金额', dataIndex: 'amount', width: 80 },
                { title: '是否打款', dataIndex: 'is_paid', width: 70 },
                { title: '主体', dataIndex: 'supplier_name', width: 100 },
                { title: '打款主体', dataIndex: 'payee_supplier', width: 100 },
                { title: '收款主体', dataIndex: 'pay_supplier', width: 100 },
                { title: '备注', dataIndex: 'remark', width: 120 }
              ]} size="small" pagination={false} scroll={{ x: 800 }} />
            </div>
          )}
          {importResult && (
            <Alert type={importResult.error > 0 ? 'warning' : 'success'}
              message={`导入完成：成功 ${importResult.success} 条，失败 ${importResult.error} 条`}
              description={importResult.errors && importResult.errors.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>第{e.row}行: {e.error}</li>)}
                </ul>
              )}
            />
          )}
          <div style={{ color: '#999', fontSize: 12 }}>Excel表头格式：小组 | 结算日期 | 金额 | 是否打款 | 是否开票 | 是否回单 | 主体 | 打款主体 | 收款主体</div>
        </Space>
      </Modal>
    </div>
  );
}

export default BillInvoice;
